import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { Address, AddressPayload, Country, Supplier, SupplierGroup, SupplierPayload } from '../../../../models/supplierAdministration';
import { supplierAdministrationApi } from '../../../../services/supplierAdministrationApi';
import AddressDialog from './AddressDialog';

type SupplierDialogProps = {
	open: boolean;
	supplier: Supplier | null;
	groups: SupplierGroup[];
	canCreateAddress: boolean;
	onClose: () => void;
	onSubmit: (payload: SupplierPayload) => Promise<void>;
}

const formatAddress = (address: Address) => {
	return `${address.name} - ${address.postalCode} ${address.city}`;
};

const SupplierDialog = ({ open, supplier, groups, canCreateAddress, onClose, onSubmit }: SupplierDialogProps) => {
	const [name, setName] = useState('');
	const [code, setCode] = useState('');
	const [groupId, setGroupId] = useState('');
	const [addressSearch, setAddressSearch] = useState('');
	const [addressOptions, setAddressOptions] = useState<Address[]>([]);
	const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
	const [addressesLoading, setAddressesLoading] = useState(false);
	const [countries, setCountries] = useState<Country[]>([]);
	const [countriesLoading, setCountriesLoading] = useState(false);
	const [addressDialogOpen, setAddressDialogOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setName(supplier?.name || '');
		setCode(supplier?.code || '');
		setGroupId(supplier?.group?.id ? String(supplier.group.id) : '');
		setAddressSearch('');
		setSelectedAddress(null);
		setAddressDialogOpen(false);
		setError(null);
	}, [supplier, open]);

	useEffect(() => {
		if (!open || !supplier?.addressId) return;

		let isMounted = true;
		supplierAdministrationApi.getAddress(supplier.addressId)
			.then((address) => {
				if (!isMounted) return;
				setSelectedAddress(address);
				setAddressOptions((current) => current.some((item) => item.id === address.id) ? current : [address, ...current]);
			})
			.catch(() => {
				if (isMounted) setError("L'adresse actuellement rattachée n'a pas pu être chargée.");
			});

		return () => {
			isMounted = false;
		};
	}, [open, supplier?.addressId]);

	useEffect(() => {
		if (!open) return;

		const timeout = window.setTimeout(() => {
			setAddressesLoading(true);
			supplierAdministrationApi.listAddresses({
				page: 1,
				limit: 20,
				searchTerm: addressSearch.trim() || undefined,
			})
				.then((response) => {
					setAddressOptions((current) => {
						const next = [...response.data];
						if (selectedAddress && !next.some((address) => address.id === selectedAddress.id)) {
							next.unshift(selectedAddress);
						}
						return next;
					});
				})
				.catch(() => setError('Erreur lors de la recherche des adresses.'))
				.finally(() => setAddressesLoading(false));
		}, 300);

		return () => window.clearTimeout(timeout);
	}, [addressSearch, open, selectedAddress]);

	useEffect(() => {
		if (!open || !canCreateAddress || countries.length > 0) return;

		let isMounted = true;
		setCountriesLoading(true);
		supplierAdministrationApi.listCountries()
			.then((countries) => {
				if (isMounted) setCountries(countries);
			})
			.catch(() => {
				if (isMounted) setError('Erreur lors de la récupération du référentiel des pays.');
			})
			.finally(() => {
				if (isMounted) setCountriesLoading(false);
			});

		return () => {
			isMounted = false;
		};
	}, [canCreateAddress, countries.length, open]);

	const selectedGroup = useMemo(
		() => groups.find((group) => String(group.id) === groupId) || null,
		[groups, groupId]
	);

	const validate = () => {
		if (!name.trim()) return 'Le nom est obligatoire.';
		if (code.trim().length !== 5) return 'Le code doit contenir exactement 5 caractères.';
		if (!selectedGroup) return 'Le groupe est obligatoire.';
		if (!selectedGroup.isActive && selectedGroup.id !== supplier?.group?.id) return 'Le groupe sélectionné est désactivé.';
		return null;
	};

	const submit = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}

		setLoading(true);
		setError(null);
		try {
			await onSubmit({
				name: name.trim(),
				code: code.trim().toUpperCase(),
				groupId: Number(groupId),
				addressId: selectedAddress?.id || null,
			});
			onClose();
		} catch (error: any) {
			setError(error?.message || 'Une erreur est survenue.');
		} finally {
			setLoading(false);
		}
	};

	const createAddress = async (payload: AddressPayload) => {
		const createdAddress = await supplierAdministrationApi.createAddress(payload);
		setSelectedAddress(createdAddress);
		setAddressOptions((current) => current.some((address) => address.id === createdAddress.id) ? current : [createdAddress, ...current]);
		toast.success("L'adresse a bien été créée.", { position: 'bottom-center', theme: 'colored' });
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>{supplier ? 'Modifier le fournisseur' : 'Créer un fournisseur'}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 0.5 }}>
						{error && (
							<Grid size={12}>
								<Alert severity="error">{error}</Alert>
							</Grid>
						)}
						<Grid size={{ xs: 12, md: 8 }}>
							<TextField
								label="Nom"
								value={name}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
								fullWidth
								required
								inputProps={{ maxLength: 150 }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 4 }}>
							<TextField
								label="Code"
								value={code}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value.toUpperCase())}
								fullWidth
								required
								inputProps={{ maxLength: 5 }}
								helperText="5 caractères"
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<Autocomplete
								options={groups}
								value={selectedGroup}
								onChange={(_, newValue) => setGroupId(newValue ? String(newValue.id) : '')}
								getOptionLabel={(option) => `${option.code} - ${option.name}${option.isActive ? '' : ' (inactif)'}`}
								getOptionDisabled={(option) => !option.isActive && option.id !== supplier?.group?.id}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								renderInput={(params) => <TextField {...params} label="Groupe" required />}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<Autocomplete
								options={addressOptions}
								value={selectedAddress}
								onChange={(_, newValue) => setSelectedAddress(newValue)}
								onInputChange={(_, newInputValue) => setAddressSearch(newInputValue)}
								getOptionLabel={(option) => formatAddress(option)}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								loading={addressesLoading}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Adresse"
										helperText="Champ optionnel"
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{addressesLoading ? <CircularProgress color="inherit" size={20} /> : null}
													{params.InputProps.endAdornment}
												</>
											),
										}}
									/>
								)}
							/>
						</Grid>
						{canCreateAddress && (
							<Grid size={12}>
								<Button
									variant="outlined"
									startIcon={countriesLoading ? <CircularProgress size={16} /> : <AddIcon />}
									onClick={() => setAddressDialogOpen(true)}
									disabled={countriesLoading || loading}
								>
									Nouvelle adresse
								</Button>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
						Annuler
					</Button>
					<Button variant="contained" onClick={submit} disabled={loading}>
						Enregistrer
					</Button>
				</DialogActions>
			</Dialog>
			<AddressDialog
				open={addressDialogOpen}
				addressToEdit={null}
				countries={countries}
				onClose={() => setAddressDialogOpen(false)}
				onSubmit={createAddress}
			/>
		</>
	);
};

export default SupplierDialog;
